import React from 'react';
import { useHistory } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import Card from '../../components/ui/card';
import DashboardCardHeader from '../../components/ui/DashboardCardHeader';
import SubmissionCalendarIcon from '../../components/ui/icons/SubmissionCalendarIcon';
import useSubmissionFormStats from '../../hooks/useSubmissionFormStats';

// Constants for submission urgency thresholds
const SUBMISSION_THRESHOLDS = {
	URGENT_COUNT: 10,
	CRITICAL_COUNT: 20,
};

const PendingSubmissionsCard = () => {
	const history = useHistory();

	// Use the custom hook for form breakdown data
	const {
		formBreakdown,
		loading,
		error: breakdownError
	} = useSubmissionFormStats({
		status: 'pending'
	});

	// Calculate total pending count from the form breakdown data
	const pendingCount = formBreakdown.reduce((sum, form) => sum + form.count, 0);

	const handleClick = () => {
		history.push('/admin/submissions?filter=pending');
	};

	const handleKeyDown = (event) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			handleClick();
		}
	};

	return (
		<Card
			className='overflow-hidden cursor-pointer hover:shadow-lg transition-shadow h-full focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			tabIndex={0}
			role="button"
			aria-label={`View ${loading ? '' : pendingCount} pending submissions. Press Enter to navigate to submissions page.`}>
			<div className='p-4 sm:p-6 h-full flex flex-col'>
				{/* Header Section */}
				<DashboardCardHeader
					title="Pending Submissions"
					value={pendingCount}
					isLoading={loading}
					icon={
						<SubmissionCalendarIcon 
							className='h-7 w-7 text-clientBlack' 
							date={new Date().getDate()}
							urgencyLevel={pendingCount > SUBMISSION_THRESHOLDS.URGENT_COUNT ? "urgent" : "normal"}
							showPinkAccent={pendingCount > SUBMISSION_THRESHOLDS.CRITICAL_COUNT}
						/>
					}
				/>
				
				{/* Urgency indicator */}
				{!loading && pendingCount > 0 && (
					<div className='flex items-center space-x-2 mb-6'>
						{pendingCount > SUBMISSION_THRESHOLDS.CRITICAL_COUNT ? (
							<>
								<div className='w-2 h-2 bg-red-500 rounded-full'></div>
								<span className='text-xs font-montserrat-medium text-red-600'>Critical</span>
							</>
						) : pendingCount > SUBMISSION_THRESHOLDS.URGENT_COUNT ? (
							<>
								<div className='w-2 h-2 bg-orange-500 rounded-full'></div>
								<span className='text-xs font-montserrat-medium text-orange-600'>Urgent</span>
							</>
						) : (
							<>
								<div className='w-2 h-2 bg-green-500 rounded-full'></div>
								<span className='text-xs font-montserrat-medium text-green-600'>Normal</span>
							</>
						)}
					</div>
				)}

				{/* Form Type Breakdown */}
				{!loading && pendingCount > 0 && (
					<div className='mb-6'>
						<div className='text-xs font-montserrat-medium text-gray-600 mb-3 uppercase tracking-wide'>
							Submission Types
						</div>
						{breakdownError ? (
							<div className='text-xs font-montserrat-regular text-gray-500 py-2'>
								Unable to load breakdown
							</div>
						) : formBreakdown && formBreakdown.length > 0 ? (
							<div className='space-y-2'>
								{formBreakdown.filter(form => form.count > 0).map((form, index) => (
									<div key={`${form.type}-${index}`} className='flex items-center justify-between'>
										<div className='flex items-center space-x-2'>
											<div className={`w-2 h-2 rounded-full ${form.color}`}></div>
											<span className='text-xs font-montserrat-regular text-gray-700'>
												{form.type}
											</span>
										</div>
										<span className='text-xs font-montserrat-medium text-gray-900'>
											{form.count}
										</span>
									</div>
								))}
							</div>
						) : (
							<div className='text-xs font-montserrat-regular text-gray-500 py-2'>
								No submissions found
							</div>
						)}
					</div>
				)}

				{/* Time-based Insight */}
				<div className='mt-auto pt-4 border-t border-gray-100'>
					<div className='flex items-center justify-between text-xs'>
						<span className='font-montserrat-regular text-gray-500'>
							{!loading ? `${formBreakdown.length} form types` : 'Loading...'}
						</span>
						<FiArrowRight className='h-4 w-4 text-gray-400' />
					</div>
				</div>
			</div>
		</Card>
	);
};

export default PendingSubmissionsCard;
